import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { toast } from 'react-hot-toast';

export interface ProdutoConfig {
  nome: string;
  tipo: 'suplemento' | 'curso' | 'serviço' | 'produto físico' | 'delivery' | 'outro';
  foco: string;
  escopo: string;
  linkCompra: string;
  palavrasProibidas: string[];
}

/**
 * Hook para gerenciar configuração de produto
 * ✅ 100% DINÂMICO - Carrega/salva via API
 */
export const useProdutoConfig = (userId: string | null) => {
  const queryClient = useQueryClient();

  // Query para carregar config
  const { data: produto, isLoading, error, dataUpdatedAt } = useQuery<ProdutoConfig>({
    queryKey: ['produto-config', userId],
    queryFn: async () => {
      if (!userId) throw new Error('userId é obrigatório');

      console.log('[useProdutoConfig] Carregando produto do backend...');
      const response = await api.get(`/api/config/produto/${userId}`);
      console.log('[useProdutoConfig] ✅ Produto carregado do backend:', response.data);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    // ✅ Garantir que refetch após reset realmente busque dados novos
    refetchOnMount: true,
  });

  // Mutation para salvar config
  const saveProduto = useMutation({
    mutationFn: async (config: ProdutoConfig) => {
      if (!userId) throw new Error('userId é obrigatório');

      console.log('[useProdutoConfig] Salvando produto:', config);
      const response = await api.post(`/api/config/produto/${userId}`, config);
      return response.data;
    },
    onSuccess: () => {
      // Invalida apenas configs de produto (NÃO scripts!)
      // Scripts só devem ser regenerados quando explicitamente solicitado
      queryClient.invalidateQueries({ queryKey: ['produto-config', userId] });

      // 🔥 Feedback de sync em tempo real
      toast.success('✅ Salvo! Bot já está usando as novas configurações', {
        duration: 3000,
        icon: '⚡',
      });
    },
    onError: (error: any) => {
      console.error('[useProdutoConfig] Erro ao salvar:', error);
      toast.error('❌ Erro ao salvar produto: ' + (error.response?.data?.message || error.message));
    },
  });

  return {
    produto: produto ?? createDefaultProduto(),
    isLoading,
    error,
    saveProduto: saveProduto.mutate,
    isSaving: saveProduto.isPending,
  };
};

function createDefaultProduto(): ProdutoConfig {
  return {
    nome: 'Monjarim',
    tipo: 'suplemento',
    foco: 'controle de apetite, redução de medidas, mais energia, maior autoestima',
    escopo: 'falar apenas de emagrecimento e compra',
    linkCompra: '',
    palavrasProibidas: [
      'perfume',
      'fragrância',
      'floral',
      'amadeirado',
      'pele',
      'cabelo',
      'unhas',
      'cosmético',
    ],
  };
}
